import type { IntlShape } from "react-intl"
import type { Provider } from "./types"

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

// ⚠ THE IMMINENCE RULE IS NOT HERE ANY MORE. `IMMINENT_THRESHOLD_MINUTES`,
// `isAppointmentImminent` and `getImminentThresholdInstant` were hoisted to
// `@internal/constants/telemedicine/imminence` by CONAPP-353, unchanged, and this file's callers
// import them from there. The reason is that `@internal/consumer`'s plan card needs the SAME line
// (its `scheduled-imminent` stage) and cannot import this package — its deps are
// `@internal/constants` and `@internal/ui` only. Do not re-add a local copy: a second definition of
// "imminent" is exactly what would let the rail and the plan card disagree about one visit.

export const formatProviderInitials = (name: string | null | undefined): string => {
  const [firstName, lastName] = (name ?? "").trim().split(" ")
  const firstInitial = firstName?.charAt(0).toUpperCase() ?? ""
  const lastInitial = lastName?.charAt(0).toUpperCase() ?? ""
  return `${firstInitial}${lastInitial}`
}

/**
 * Composes a provider's display name out of the parts the record actually carries.
 *
 * The honorific is data, not something derivable from `name`/`title`: frame 5 draws "Jane Doe, MD"
 * directly beside "Dr. Susanna Park, MD", so the identical `{ name, title: "MD" }` shape has to be
 * able to render both ways. This previously prefixed `Dr. ` unconditionally and got Jane Doe wrong.
 *
 * Provisional pending design/API: the real API may send an honorific, a credential, or a ready-made
 * display name. Whichever it is, the decision belongs to the record and not to this function.
 */
export const formatProviderName = (
  provider: Pick<Provider, "honorific" | "name" | "title"> | null | undefined
): string => {
  const named = [provider?.honorific, provider?.name].filter(Boolean).join(" ")
  return provider?.title ? `${named}, ${provider.title}` : named
}

/**
 * "August 31" — the short form the cancel dialog uses.
 *
 * No `timeZone` option anywhere below: an upcoming appointment's `date` is an instant, so the
 * browser's own zone is the member's zone. See the note on `UpcomingAppointment` in `types.ts`.
 *
 * The design writes "August 31st". `Intl.DateTimeFormat` has no ordinal day, and hand-rolling "st /
 * nd / rd / th" would put English grammar back into a formatter this ticket exists to take it out
 * of, so the ordinal is dropped deliberately.
 */
export const formatAppointmentDate = (intl: IntlShape, date: Date): string =>
  intl.formatDate(date, { month: "long", day: "numeric" })

/** "10:30 AM" in the member's own zone. */
export const formatAppointmentTime = (intl: IntlShape, date: Date): string =>
  intl.formatTime(date, { hour: "numeric", minute: "2-digit" })

/** "Fri, Jun 12" in the member's own zone. */
export const formatPastAppointmentDate = (intl: IntlShape, date: Date): string =>
  intl.formatDate(date, { weekday: "short", month: "short", day: "numeric" })

/** Whether two instants land on the same day in the member's own zone. */
export const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** "September 7, 2026" — carries the year, where `formatAppointmentDate` does not. */
export const formatAppointmentDateWithYear = (intl: IntlShape, date: Date): string =>
  intl.formatDate(date, { year: "numeric", month: "long", day: "numeric" })

/**
 * "11:00 AM PST".
 *
 * Named the "home" formats until MEM-848, on the reading that the tiles show one line of detail with
 * nothing to disambiguate against while the Appointments page deliberately went without — a comment
 * here flagged that as worth confirming with design. The Q3 telehealth board confirms the opposite:
 * every Appointments frame reads "September 8, 2026 · 11:00 AM PST", so the page names the zone and
 * carries the year too, and these are simply the appointment formats.
 */
export const formatAppointmentTimeWithZone = (intl: IntlShape, date: Date): string =>
  intl.formatTime(date, { hour: "numeric", minute: "2-digit", timeZoneName: "short" })

/**
 * "in 10 minutes", or "now" once the start time has passed — `isAppointmentImminent` stays true after
 * it, and "2 minutes ago" under the caller's "Starting" prefix would be nonsense.
 */
export const formatStartingIn = (intl: IntlShape, date: Date, now: Date = new Date()): string => {
  const minutes = Math.round((date.getTime() - now.getTime()) / MINUTE_MS)
  return minutes <= 0 ? "now" : intl.formatRelativeTime(minutes, "minute")
}

/**
 * The countdown chip: "in 3 days", "in 2 hours", "in 5 minutes".
 *
 * Delegated to `formatRelativeTime` so the unit name and its plural form come from the locale's CLDR
 * data — "in 1 day" vs "in 3 days" is a translation concern, not a string concat. The chip's
 * uppercase rendering comes from `theme.typography.chip`, which is where the design's "IN 3 DAYS"
 * casing comes from rather than from this string.
 *
 * Frame 4 writes "In 5 mins"; en-US CLDR's long form is "in 5 minutes". Abbreviating is the locale's
 * call, not ours, so the long form stands and the copy difference is a question for design.
 */
export const formatCountdownLabel = (intl: IntlShape, date: Date, now: Date = new Date()): string => {
  const differenceMs = date.getTime() - now.getTime()
  const distanceMs = Math.abs(differenceMs)

  if (distanceMs >= DAY_MS) {
    return intl.formatRelativeTime(Math.round(differenceMs / DAY_MS), "day")
  }
  if (distanceMs >= HOUR_MS) {
    return intl.formatRelativeTime(Math.round(differenceMs / HOUR_MS), "hour")
  }
  return intl.formatRelativeTime(Math.round(differenceMs / MINUTE_MS), "minute")
}
